import {
  Box,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoading } from "../../../../providers/loadingProvider";
import {
  institutionApplicantsSelector,
  institutionSelector,
  programsSelector,
  setInstitutionApplicants,
} from "../../../../store/researchauthoritiesSlice";
import { useQuery } from "../../../customHooks";
import ApplicationsTable from "../../Components/applicants/applications/ApplicationsTable";
import {
  StyledTab,
  StyledTabs,
  TabPanel,
} from "../../Components/SecondaryTabs";
import { proposalEligibilityStatus } from "../../Constants";
import service from "../../Helpers/gifservices";
const PAGE_TITLE = "Applications";
const DETAILS = 
`Dear colleagues,

This screen allows you to view applications awaiting for approval and submission, along with those you have either unlocked or already submitted to GIF.

If any changes to the application are necessary, you can use the “Unlock” option and instruct the applicant to make the required corrections (please note that this must be done before you approve and submit the application). 
In this view, you will approve ONLY applications where the Applicant Leader is affiliated with your institute.
You will be able to view the applications that are awaiting for approval only after the applicants submit it through the submission portal.
If you have any questions, please do not hesitate to contact us at: gif.admin@gif.org.il
`;

const useStyles = makeStyles((theme) => ({
  infoIcon: {
    marginLeft: 5,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  tabs: {
    margin: 5,
    marginTop: 10,
  },
  formControl:{
    minWidth:"20%",
  }
}));

export default function Applications() {
  const classes = useStyles();
  const loading = useLoading();
  const query = useQuery();
  const dispatch = useDispatch();
  const institution = useSelector(institutionSelector);
  const institutionApplicants = useSelector(institutionApplicantsSelector);
  const [getFromUrl, setGetFromUrl] = useState(false);
  const [program, setProgram] = useState(``);
  const [selectedTab, setSelectedTab] = useState(0);
  const [proposals, setProposals] = useState({
    wating: [],
    unlocked: [],
    submit: [],
  });
  const programTypes = useSelector(programsSelector);

  const createPDF = async (proposalId, proposalNumber) => {
    if (!Boolean(proposalId)) return;
    const loadingId = loading.addLoading("Downloading Proposal Info");
    const pdfPath = await service.createProposalPDF({ proposalId });
    await service.downloadRawFile({
      path: pdfPath,
      filename: proposalNumber,
    });
    loading.removeLoading(loadingId);
  };

  const UpdateProposalNote = async (proposalId, note) => {
    const loadingId = loading.addLoading("Updating Note");
    await service.UpdateProposalRAnote({ proposalId, note });
    getProposals();
    loading.removeLoading(loadingId);
  };

  useEffect(() => {
    if (getFromUrl===true) return;
    const tab = query.get("tab");
    const program = query.get("program");
    if (program !== null && tab !== null) {
      setProgram(parseInt(program));
      setSelectedTab(parseInt(tab));
      setGetFromUrl(true);
    } else if (programTypes !== null) {
      setProgram(parseInt(programTypes[0].number));
    }
  }, [query, getFromUrl, programTypes]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const programChanged = (e) => {
    setProgram(e.target.value);
  };

  const getProposals = useCallback(async () => {
    if (institutionApplicants === null) return;
    const proposalsIds = institutionApplicants
      .filter((a) => a.programTypeNumber === program)
      .map((a) => a.proposalId);
    if (proposalsIds.length === 0) {
      setProposals({ wating: [], unlocked: [], submit: [] });
      return;
    }
    const loadingId = loading.addLoading("Loading Applications");
    const proposals = await service.GetProposals({ proposalsIds });
    const wating = proposals.filter(
      (p) =>
        p.proposalStatus ===
        proposalEligibilityStatus.submittedByApplicationLeader
    );
    const unlocked = proposals.filter(
      (p) => p.proposalStatus === proposalEligibilityStatus.unlockedByRA
    );
    const submit = proposals.filter(
      (p) =>
        p.proposalStatus === proposalEligibilityStatus.approvedByRA ||
        p.proposalStatus === proposalEligibilityStatus.approvedByGIF
    );
    setProposals({ wating: wating, unlocked: unlocked, submit: submit });
    loading.removeLoading(loadingId);
  }, [institutionApplicants, program, loading]);

  const updateProposalStatus = async (proposalId, status) => {
    const loadingId = loading.addLoading("Updating");
    await service.UpdateProposalStatus({ proposalId, status });
    getProposals();
    loading.removeLoading(loadingId);
    refreshInstitutionApplicants();
  };

  const refreshInstitutionApplicants = () => {
    const institutionId = institution?.id;
    if (institutionId === null || institutionId === undefined) return;
    service
      .GetInstitutionApplicants({ institutionId })
      .then((institutionApplicants) => {
        dispatch(setInstitutionApplicants(institutionApplicants));
      });
  };

  useEffect(() => {
    getProposals();
  }, [getProposals]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box className={classes.header}>
          <Typography variant="subtitle2">{PAGE_TITLE}</Typography>
          <InfoOutlinedIcon className={classes.infoIcon} color="primary" />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <pre style={{ fontFamily: "inherit",whiteSpace: 'break-spaces' }}>{DETAILS}</pre>
      </Grid>
      <Grid item xs={12}>
        <FormControl className={classes.formControl}>
          <InputLabel>Program</InputLabel>
          <Select value={program} onChange={programChanged} label="Program">
            {programTypes.map((a) => (
              <MenuItem key={a.number} value={a.number}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <StyledTabs
          value={selectedTab}
          className={classes.tabs}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="simple tabs example"
        >
          <StyledTab label="Applications waiting for submission" />
          <StyledTab label="Unlocked Applications" />
          <StyledTab label="Applications submitted to GIF" />
        </StyledTabs>
        <Divider style={{ marginTop: -6 }} />
        <TabPanel value={selectedTab} index={0}>
          <ApplicationsTable
            updateApplicantionNote={UpdateProposalNote}
            createPDF={createPDF}
            tableType="waiting"
            applications={proposals.wating}
            updateProposalStatus={updateProposalStatus}
          />
        </TabPanel>
        <TabPanel value={selectedTab} index={1}>
          <ApplicationsTable
            updateApplicantionNote={UpdateProposalNote}
            createPDF={createPDF}
            tableType="unlocked"
            applications={proposals.unlocked}
            updateProposalStatus={updateProposalStatus}
          />
        </TabPanel>
        <TabPanel value={selectedTab} index={2}>
          <ApplicationsTable
            updateApplicantionNote={UpdateProposalNote}
            createPDF={createPDF}
            tableType="submit"
            applications={proposals.submit}
            updateProposalStatus={updateProposalStatus}
          />
        </TabPanel>
      </Grid>
    </Grid>
  );
}
